/**
 * Spatial Analysis Redux Slice
 *
 * Manages state for spatial analysis tools including buffer, intersection,
 * union, clip, and spatial query operations.
 *
 * **Validates: Requirements 22.1, 23.1, 24.1, 25.1, 26.1, 26.4**
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Feature } from 'geojson';
import type { DistanceUnits, SpatialRelationship } from '../../services/SpatialAnalysisEngine';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SpatialTool = 'buffer' | 'intersection' | 'union' | 'clip' | 'spatial-query' | null;

export interface BufferToolState {
  distance: number;
  units: DistanceUnits;
  rings: number;
  sourceLayerId: string | null;
}

export interface IntersectionToolState {
  sourceLayerId: string | null;
  targetLayerId: string | null;
}

export interface UnionToolState {
  selectedLayerIds: string[];
}

export interface ClipToolState {
  inputLayerId: string | null;
  clipLayerId: string | null;
}

export interface SpatialQueryToolState {
  referenceFeatureId: string | null;
  referenceLayerId: string | null;
  targetLayerId: string | null;
  relationship: SpatialRelationship;
  distance: number;
  distanceUnits: DistanceUnits;
}

export interface SpatialAnalysisState {
  activeTool: SpatialTool;
  isProcessing: boolean;
  error: string | null;
  message: string | null;

  // Tool-specific state
  bufferTool: BufferToolState;
  intersectionTool: IntersectionToolState;
  unionTool: UnionToolState;
  clipTool: ClipToolState;
  spatialQueryTool: SpatialQueryToolState;

  // Query results
  queryResults: Feature[];
  highlightedFeatureIds: string[];
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState: SpatialAnalysisState = {
  activeTool: null,
  isProcessing: false,
  error: null,
  message: null,

  bufferTool: {
    distance: 100,
    units: 'meters',
    rings: 1,
    sourceLayerId: null,
  },

  intersectionTool: {
    sourceLayerId: null,
    targetLayerId: null,
  },

  unionTool: {
    selectedLayerIds: [],
  },

  clipTool: {
    inputLayerId: null,
    clipLayerId: null,
  },

  spatialQueryTool: {
    referenceFeatureId: null,
    referenceLayerId: null,
    targetLayerId: null,
    relationship: 'intersects',
    distance: 100,
    distanceUnits: 'meters',
  },

  queryResults: [],
  highlightedFeatureIds: [],
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const spatialAnalysisSlice = createSlice({
  name: 'spatialAnalysis',
  initialState,
  reducers: {
    // Tool activation
    setActiveTool: (state, action: PayloadAction<SpatialTool>) => {
      state.activeTool = action.payload;
      state.error = null;
      state.message = null;
    },

    closeTool: (state) => {
      state.activeTool = null;
      state.error = null;
      state.message = null;
      state.queryResults = [];
      state.highlightedFeatureIds = [];
    },

    // Processing state
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isProcessing = false;
    },

    setMessage: (state, action: PayloadAction<string | null>) => {
      state.message = action.payload;
    },

    // Buffer tool
    setBufferDistance: (state, action: PayloadAction<number>) => {
      state.bufferTool.distance = action.payload;
    },

    setBufferUnits: (state, action: PayloadAction<DistanceUnits>) => {
      state.bufferTool.units = action.payload;
    },

    setBufferRings: (state, action: PayloadAction<number>) => {
      state.bufferTool.rings = action.payload;
    },

    setBufferSourceLayer: (state, action: PayloadAction<string | null>) => {
      state.bufferTool.sourceLayerId = action.payload;
    },

    // Intersection tool
    setIntersectionSourceLayer: (state, action: PayloadAction<string | null>) => {
      state.intersectionTool.sourceLayerId = action.payload;
    },

    setIntersectionTargetLayer: (state, action: PayloadAction<string | null>) => {
      state.intersectionTool.targetLayerId = action.payload;
    },

    // Union tool
    setUnionSelectedLayers: (state, action: PayloadAction<string[]>) => {
      state.unionTool.selectedLayerIds = action.payload;
    },

    toggleUnionLayer: (state, action: PayloadAction<string>) => {
      const idx = state.unionTool.selectedLayerIds.indexOf(action.payload);
      if (idx >= 0) {
        state.unionTool.selectedLayerIds.splice(idx, 1);
      } else {
        state.unionTool.selectedLayerIds.push(action.payload);
      }
    },

    // Clip tool
    setClipInputLayer: (state, action: PayloadAction<string | null>) => {
      state.clipTool.inputLayerId = action.payload;
    },

    setClipBoundaryLayer: (state, action: PayloadAction<string | null>) => {
      state.clipTool.clipLayerId = action.payload;
    },

    // Spatial query tool
    setSpatialQueryReferenceFeature: (
      state,
      action: PayloadAction<{ featureId: string | null; layerId: string | null }>
    ) => {
      state.spatialQueryTool.referenceFeatureId = action.payload.featureId;
      state.spatialQueryTool.referenceLayerId = action.payload.layerId;
    },

    setSpatialQueryTargetLayer: (state, action: PayloadAction<string | null>) => {
      state.spatialQueryTool.targetLayerId = action.payload;
    },

    setSpatialQueryRelationship: (state, action: PayloadAction<SpatialRelationship>) => {
      state.spatialQueryTool.relationship = action.payload;
    },

    setSpatialQueryDistance: (state, action: PayloadAction<number>) => {
      state.spatialQueryTool.distance = action.payload;
    },

    setSpatialQueryDistanceUnits: (state, action: PayloadAction<DistanceUnits>) => {
      state.spatialQueryTool.distanceUnits = action.payload;
    },

    // Query results
    setQueryResults: (state, action: PayloadAction<Feature[]>) => {
      state.queryResults = action.payload;
      state.highlightedFeatureIds = action.payload
        .map((f) => (f.id as string) || (f.properties?.id as string) || '')
        .filter(Boolean);
    },

    clearQueryResults: (state) => {
      state.queryResults = [];
      state.highlightedFeatureIds = [];
    },

    setHighlightedFeatures: (state, action: PayloadAction<string[]>) => {
      state.highlightedFeatureIds = action.payload;
    },
  },
});

export const {
  setActiveTool,
  closeTool,
  setProcessing,
  setError,
  setMessage,
  setBufferDistance,
  setBufferUnits,
  setBufferRings,
  setBufferSourceLayer,
  setIntersectionSourceLayer,
  setIntersectionTargetLayer,
  setUnionSelectedLayers,
  toggleUnionLayer,
  setClipInputLayer,
  setClipBoundaryLayer,
  setSpatialQueryReferenceFeature,
  setSpatialQueryTargetLayer,
  setSpatialQueryRelationship,
  setSpatialQueryDistance,
  setSpatialQueryDistanceUnits,
  setQueryResults,
  clearQueryResults,
  setHighlightedFeatures,
} = spatialAnalysisSlice.actions;

export default spatialAnalysisSlice.reducer;
